<template>
  <div class="phone-input-container">
    <div class="row">
      <!-- Custom dropdown with flags and key navigation -->
      <div class="col-4 pe-0">
        <div
          class="custom-dropdown bg-white border-color"
          @click="toggleDropdown"
          @keydown="onKeyPress"
          ref="dropdown"
          tabindex="0"
        >
          <div class="selected-country">
            <img
              :src="getFlagUrl(selectedCountryCode)"
              class="flag-icon"
              alt="Flag"
            />
            <span class="dial-code">{{ currentCountry.dialCode }}</span>
            <span class="dropdown-arrow">
              <i class="fa fa-angle-down nav-color angle-right-icon" aria-hidden="true"></i>
            </span>
          </div>

          <ul v-if="dropdownOpen" class="dropdown-list">
            <li
              v-for="country in countries"
              :key="country.code"
              :id="`country-option-${country.code}`"
              class="dropdown-item"
              @click.stop="selectCountry(country.code)"
            >
              <img
                :src="getFlagUrl(country.code)"
                class="flag-icon"
                alt="Flag"
              />
              {{ country.name }} ({{ country.dialCode }})
            </li>
          </ul>
        </div>
      </div>

      <!-- Phone input -->
      <div class="col-8 pe-xl-0">
        <div class="w-100 d-inline-block customer-form">
          <input
            v-model="phoneNumber"
            :placeholder="placeholderText"
            type="tel"
            class="form-control w-100 bg-white text-size-16 h-40 padding-left-3 padding-right-3 border rounded-2 text-full-12 border-color"
            @input="onPhoneInput"
            autocomplete="tel"
            :maxlength="currentCountry.maxLength + 5"
          />
        </div>
      </div>
    </div>

    <!-- Feedback -->
    <!-- <div v-if="phoneNumber" class="text-size-12 text-info margin-top-1">
      Typing: "{{ phoneNumber }}" (Length: {{ phoneNumber.replace(/\D/g, '').length }})
    </div> -->

    <div v-if="phoneNumber && phoneNumber.length > 0" class="validation-message margin-top-1">
      <span v-if="isValidPhone" class="text-success text-size-12">
        ✅ Valid number for {{ currentCountry.name }}
      </span>
      <span v-else class="text-danger text-size-12">
        ❌ Enter valid number for {{ currentCountry.name }}
      </span>
    </div>

    <!-- <div v-if="phoneNumber && phoneNumber.length > 0" class="text-size-12 text-muted margin-top-1">
      Full Number: {{ fullPhoneNumber }}
    </div> -->
  </div>
</template>

<script>
import { countries } from '../utils/countries.js';

export default {
  name: 'PhoneNumberInput',
  props: {
    modelValue: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'validation-changed'],
  data() {
    return {
      selectedCountryCode: 'AE',
      phoneNumber: '',
      countries,
      dropdownOpen: false,
      searchString: '',
      searchTimeout: null
    };
  },
  computed: {
    currentCountry() {
      return this.countries.find(c => c.code === this.selectedCountryCode) || this.countries[0];
    },
    placeholderText() {
      return this.currentCountry.sampleNumber;
    },
    fullPhoneNumber() {
      const clean = this.phoneNumber.replace(/\D/g, '');
      return `${this.currentCountry.dialCode}${clean}`;
    },
    isValidPhone() {
      const num = this.phoneNumber.replace(/\D/g, '');
      const { minLength, maxLength } = this.currentCountry;
      return num.length >= minLength && num.length <= maxLength;
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal !== this.fullPhoneNumber) {
          const dialCode = this.currentCountry.dialCode;
          if (newVal.startsWith(dialCode)) {
            this.phoneNumber = newVal.substring(dialCode.length);
          } else {
            this.phoneNumber = newVal;
          }
        }
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    getFlagUrl(code) {
      return `https://cdn.jsdelivr.net/npm/country-flag-icons/3x2/${code}.svg`;
    },
    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen;
    },
    selectCountry(code) {
      this.selectedCountryCode = code;
      this.phoneNumber = '';
      this.dropdownOpen = false;
      this.emitUpdate();
    },
    onPhoneInput(event) {
      const clean = event.target.value.replace(/[^\d\s\-()]/g, '');
      this.phoneNumber = clean;
      this.emitUpdate();
    },
    emitUpdate() {
      const full = this.fullPhoneNumber;
      this.$emit('update:modelValue', full);
      this.$emit('validation-changed', this.isValidPhone);
    },
    onKeyPress(event) {
      if (!this.dropdownOpen) return;
      const key = event.key.toLowerCase();
      if (key.length !== 1 || !key.match(/[a-z]/)) return;

      clearTimeout(this.searchTimeout);
      this.searchString += key;

      const match = this.countries.find(c =>
        c.name.toLowerCase().startsWith(this.searchString)
      );

      if (match) {
        this.scrollToCountry(match.code);
      }

      this.searchTimeout = setTimeout(() => {
        this.searchString = '';
      }, 800);
    },
    scrollToCountry(code) {
      this.$nextTick(() => {
        const element = this.$el.querySelector(`#country-option-${code}`);
        if (element) {
          element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
      });
    },
    handleClickOutside(event) {
      if (this.dropdownOpen && this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.dropdownOpen = false;
      }
    },
  }
};
</script>

<style scoped>
.custom-dropdown {
  position: relative;
  border: 1px solid #ccc;
  border-radius: .5vw;
  height: 3.1vw;
  padding: .7vw 0.5vw;
  cursor: pointer;
  user-select: none;
}
.selected-country {
  display: flex;
  align-items: center;
  font-size: 1.01vw;
  margin-top: 0.1vw;
}
.flag-icon {
  margin-right: 6px;
  width: 2vw;
  height: 1vw;
}
.dial-code {
  margin-right: auto;
}
.dropdown-arrow {
  margin-left: auto;
}
.dropdown-list {
  padding-left:0 !important;
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  width: 200px;
  border: 1px solid #ccc;
  border-top: none;
}
.dropdown-item {
  padding: 6px 10px;
  display: flex;
  align-items: center;
  cursor: pointer;
}
.dropdown-item:hover {
  background-color: #1E88E5 !important;
  color:#fff !important;
}

</style>
